import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

import type { FilterableColumnIds } from './TableFilters.types';

import { Combobox } from '../Form/Combobox/Combobox';
import { Field } from '../Form/Field/Field';
import { Select } from '../Form/Select/Select';
import { useTableContext } from './TableRoot.context';

type FilterOption = { label: string; value: string | number | boolean };

export type Props = {
  columnId: FilterableColumnIds;
  /**
   * Hides the filter based on the number of options:
   * - `'noOptions'`: hide when there are no options.
   * - `'singleOption'`: hide when there are 0 or 1 options, as there is no meaningful choice to make.
   */
  hideWhen?: 'noOptions' | 'singleOption';
  type: 'select' | 'combobox' | 'multiselect';
};

export const TableFilter = ({ columnId, hideWhen, type }: Props) => {
  const table = useTableContext();
  const column = useMemo(() => table.getColumn(columnId), [columnId, table]);
  invariant(column, `Column ${columnId} does not exist on this table.`);

  const options = column.columnDef.meta?.filterOptions;
  invariant(options, `Missing filter options for column ${columnId}`);
  const optionItems = Combobox.useItems(options, { getLabel: (option) => option.label, getValue: (option) => option.value });

  const { t } = useTranslation(['filters']);

  const filterValue = column.getFilterValue();

  if (hideWhen === 'noOptions' && options.length === 0) return null;
  if (hideWhen === 'singleOption' && options.length <= 1) return null;

  return (
    <Field.Root>
      {type === 'select' ? (
        <Select.Root items={options} onValueChange={(value) => column.setFilterValue(value)} value={filterValue ?? null}>
          <Select.Button placeholder={t(`filters:${columnId}.label`)}>{t(`filters:${columnId}.label`)}</Select.Button>

          <Select.List align="end" minWidth="200px">
            {options.map((option) => (
              <Select.Item key={String(option.value)} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.List>
        </Select.Root>
      ) : type === 'combobox' ? (
        <Combobox.Root
          items={optionItems}
          onValueChange={(value) => column.setFilterValue(value)}
          value={(filterValue as FilterOption['value'] | undefined) ?? null}
        >
          <Combobox.Button placeholder={t(`filters:${columnId}.label`)} />

          <Combobox.List align="end" minWidth="200px">
            {(option: FilterOption) => (
              <Combobox.Item key={String(option.value)} value={option.value}>
                {option.label}
              </Combobox.Item>
            )}
          </Combobox.List>
        </Combobox.Root>
      ) : (
        <Select.Root
          items={options}
          multiple
          onValueChange={(value) => column.setFilterValue(value)}
          value={Array.isArray(filterValue) ? filterValue : []}
        >
          <Select.Button placeholder={t(`filters:${columnId}.label`)}>{t(`filters:${columnId}.label`)}</Select.Button>

          <Select.List align="end" minWidth="200px">
            {options.map((option) => (
              <Select.Item key={String(option.value)} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.List>
        </Select.Root>
      )}
    </Field.Root>
  );
};
